import type { EstadoDeElemento, Fila, NivelDeRiesgo, Origen, Prelacion, Procedencia } from './tipos'

/**
 * Vocabulario de producto (CLAUDE.md): en la interfaz siempre propone
 * **Atalaya**. Nunca se nombra al proveedor, al modelo ni «IA»: la base de una
 * propuesta se declara como versión de Atalaya (p. ej. `atalaya-matriz-v1`).
 */
export const ETIQUETA_DE_ORIGEN: Record<Origen, string> = {
  ia: 'Propuesta de Atalaya',
  reglas: 'Propuesta de Atalaya (catálogo de referencia)',
  profesional: 'Registrada por el profesional',
}

export const ETIQUETA_DE_ESTADO: Record<EstadoDeElemento, string> = {
  propuesta: 'Pendiente de tu revisión',
  validada: 'Con visto bueno',
  descartada: 'Descartada',
}

export const ETIQUETA_DE_PRELACION: Record<Prelacion, string> = {
  eliminacion: 'Eliminación o supresión',
  ingenieria: 'Control de ingeniería / medida técnica',
  administrativa: 'Disposiciones administrativas',
  epp: 'Equipo de protección personal',
}

export const CLASES_DE_NIVEL: Record<NivelDeRiesgo, string> = {
  tolerable: 'bg-riesgo-tolerable-fondo text-riesgo-tolerable-texto',
  moderado: 'bg-riesgo-moderado-fondo text-riesgo-moderado-texto',
  importante: 'bg-riesgo-importante-fondo text-riesgo-importante-texto',
  intolerable: 'bg-riesgo-intolerable-fondo text-riesgo-intolerable-texto',
}

/** Resumen humano de la evaluación de una fila, sin números crudos cuando no corresponde. */
export function resumenDeEvaluacion(fila: Fila): string {
  const e = fila.evaluacion
  if (fila.version_motor !== 'evaluacion-isp-v3') return 'Reevaluación pendiente según guía ISP'
  if (e.tipo === 'motor') return `VEP ${e.vep} · ${e.nivel_etiqueta}`
  if (e.consolidada) return `${e.magnitud_exposicion ?? ''} · ${e.nivel_riesgo ?? ''}`.trim()
  return 'Pendiente de protocolo'
}

/** Base de una propuesta en una línea (toda propuesta declara su base), en lenguaje de producto. */
export function describirProcedencia(origen: Origen, procedencia: Procedencia | undefined): string {
  if (origen === 'profesional') {
    const desdeAtalaya = procedencia?.editada_desde === 'ia' || procedencia?.editada_desde === 'reglas'
    return desdeAtalaya ? 'Editada por el profesional a partir de una propuesta de Atalaya.' : 'Registrada por el profesional.'
  }
  const version = procedencia?.version_prompt ? ` (${procedencia.version_prompt})` : ''
  if (origen === 'ia') return `Propuesta de Atalaya${version}; revísala antes de dar visto bueno.`
  const desdeCatalogo = procedencia?.fallback ? ' Atalaya usó su catálogo de referencia para esta propuesta.' : ''
  return `Propuesta de Atalaya desde su catálogo de referencia${version}; revísala antes de dar visto bueno.${desdeCatalogo}`
}

export function nombreDeArchivoDeExportacion(alcance: string, revision: number, oficial: boolean): string {
  const base = alcance
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40)
  return `MIPER-${base || 'matriz'}-${oficial ? `rev${revision}` : 'borrador'}.xlsx`
}

export function plazoHumano(dias: number | null): string {
  if (dias === null) return 'Seguimiento periódico'
  if (dias === 0) return 'Inmediato: no continuar la tarea'
  return `Plazo orientativo: ${dias} días`
}
